import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle, Sparkle, ShieldCheck, Info, ArrowRight, CreditCard } from "@phosphor-icons/react";

export default function Pricing() {
  const [plans, setPlans] = useState([]);
  const [interval, setInterval] = useState("monthly");
  const [notice, setNotice] = useState("");
  const [setupFee, setSetupFee] = useState(0);
  const [checkingOut, setCheckingOut] = useState(null);
  const [current, setCurrent] = useState(null);

  useEffect(() => {
    api.get("/pricing/plans").then((r) => {
      setPlans(r.data.plans);
      setNotice(r.data.notice);
      setSetupFee(r.data.one_time_setup_fee);
    });
    api.get("/billing/status").then((r) => setCurrent(r.data));
  }, []);

  const checkout = async (plan) => {
    const lookup_key = interval === "annual" ? plan.lookup_annual : plan.lookup_monthly;
    setCheckingOut(plan.id);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setCheckingOut(null);
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-[1400px] mx-auto">
      <div className="mb-6">
        <div className="font-label text-orange-400 mb-2">PRICING</div>
        <h1 className="text-4xl md:text-5xl font-display font-black tracking-tight">Choose your plan</h1>
        <p className="text-slate-400 mt-2 max-w-2xl">
          Simple, transparent pricing that scales from one team to a full school district.
          Every plan includes automatic 90/60/30-day expiration alerts and audit-ready exports.
        </p>
      </div>

      {/* TEST-MODE notice */}
      <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 mb-6 flex items-start gap-3" data-testid="pricing-testmode-notice">
        <Info size={20} weight="bold" className="text-amber-400 shrink-0 mt-0.5" />
        <div className="text-sm text-amber-200">
          <span className="font-semibold">Stripe test mode.</span> {notice}
        </div>
      </div>

      {/* Current subscription */}
      {current?.current_plan && current.subscription_status === "active" && (
        <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4 mb-8 flex items-center gap-3" data-testid="current-plan-notice">
          <CheckCircle size={18} weight="bold" className="text-emerald-400" />
          <div className="text-sm">
            <span className="font-semibold text-emerald-200">You're on {current.current_plan.replace("auditready_", "").replace("_", " · ")}</span>
            <span className="text-slate-400"> — test-mode subscription active.</span>
          </div>
        </div>
      )}

      {/* Interval toggle */}
      <div className="flex justify-center mb-10">
        <div className="inline-flex rounded-full border border-white/10 bg-slate-900 p-1">
          {["monthly", "annual"].map((k) => (
            <button
              key={k}
              onClick={() => setInterval(k)}
              data-testid={`pricing-interval-${k}`}
              className={`px-5 py-2 rounded-full text-sm font-semibold capitalize transition-colors ${interval === k ? "bg-orange-500 text-white" : "text-slate-400 hover:text-slate-100"}`}
            >
              {k}{k === "annual" && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-white/20 text-white/90">Save ~15%</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Plan cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {plans.map((p) => {
          const price = interval === "monthly" ? p.price_monthly : Math.round(p.price_annual / 12);
          const billedTotal = interval === "annual" ? p.price_annual : null;
          return (
            <div
              key={p.id}
              data-testid={`pricing-card-${p.id}`}
              className={`rounded-2xl border p-8 relative flex flex-col ${p.highlight ? "border-orange-500/50 bg-slate-900 shadow-2xl shadow-orange-500/10" : "border-white/10 bg-slate-900"}`}
            >
              {p.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-orange-500 text-white text-xs font-bold px-3 py-1 shadow-lg shadow-orange-500/30 inline-flex items-center gap-1.5">
                  <Sparkle size={12} weight="fill" /> MOST POPULAR
                </div>
              )}
              <div className="font-label text-orange-400 mb-2">{p.tag}</div>
              <div className="font-display font-black text-2xl tracking-tight">{p.name}</div>

              <div className="mt-5 mb-6">
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-display font-black">${price}</span>
                  <span className="text-slate-400 text-sm">/mo</span>
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  {billedTotal ? `Billed $${billedTotal}/year` : "Billed monthly"}
                </div>
              </div>

              <ul className="space-y-2.5 text-sm mb-8 flex-1">
                {p.features.map((f, i) => (
                  <li key={i} className="flex gap-2 items-start">
                    <CheckCircle size={16} weight="bold" className={`shrink-0 mt-0.5 ${p.highlight ? "text-orange-400" : "text-emerald-400"}`} />
                    <span className="text-slate-300">{f}</span>
                  </li>
                ))}
              </ul>

              <Button
                data-testid={`pricing-cta-${p.id}`}
                onClick={() => checkout(p)}
                disabled={checkingOut === p.id}
                className={`w-full rounded-full h-11 font-semibold ${p.highlight ? "bg-orange-500 text-white hover:bg-orange-400" : "bg-slate-800 text-slate-100 hover:bg-slate-700 border border-white/10"}`}
              >
                {checkingOut === p.id ? "Redirecting…" : (
                  <>
                    <CreditCard size={14} weight="bold" className="mr-1.5" />
                    {p.cta}
                    <ArrowRight size={14} weight="bold" className="ml-1.5" />
                  </>
                )}
              </Button>
              <div className="text-[10px] text-center text-slate-500 mt-3 font-label">
                TEST MODE · Card 4242 4242 4242 4242
              </div>
            </div>
          );
        })}
      </div>

      {/* Setup fee note */}
      <div className="mt-8 rounded-xl border border-white/10 bg-slate-900 p-5 flex items-center gap-3">
        <ShieldCheck size={18} weight="bold" className="text-orange-400 shrink-0" />
        <div className="text-sm text-slate-400">
          <span className="text-slate-100 font-semibold">One-time ${setupFee} setup fee</span> — includes CSV migration, admin onboarding call, and audit-report template setup. Waived on annual plans.
        </div>
      </div>

      {/* FAQ style */}
      <div className="mt-10 grid md:grid-cols-2 gap-6">
        <div className="rounded-xl border border-white/10 bg-slate-900 p-6">
          <div className="font-display font-black tracking-tight text-lg mb-2">Can I switch plans later?</div>
          <p className="text-sm text-slate-400">Yes — upgrade or downgrade any time. Changes take effect at your next billing cycle.</p>
        </div>
        <div className="rounded-xl border border-white/10 bg-slate-900 p-6">
          <div className="font-display font-black tracking-tight text-lg mb-2">Is this real billing?</div>
          <p className="text-sm text-slate-400">This build runs Stripe in test mode — no real charges. Live payments are activated after the contest version is complete.</p>
        </div>
        <div className="rounded-xl border border-white/10 bg-slate-900 p-6">
          <div className="font-display font-black tracking-tight text-lg mb-2">How do you count staff members?</div>
          <p className="text-sm text-slate-400">Active roster only. Archived or removed staff don't count against your limit.</p>
        </div>
        <div className="rounded-xl border border-white/10 bg-slate-900 p-6">
          <div className="font-display font-black tracking-tight text-lg mb-2">Do you verify credentials?</div>
          <p className="text-sm text-slate-400">No. AuditReady Sports is an independent record-keeping tool. You remain responsible for verifying with issuing bodies.</p>
        </div>
      </div>
    </div>
  );
}
